import React from 'react'
import { IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonText } from '@ionic/react'
import { caretDownOutline, chevronForwardOutline, lockClosedOutline } from 'ionicons/icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { formatShortMonthDateTime } from '../../../../../utilities/dateTime.util'
import { getSelectedFlagDetailSelector } from '../../selectors'
import { getUserIdSelector, canViewFlagSelector, canAssignFlagSelector } from '../../../../../features/user/selectors'
import { isFlagViewCreatorBlackedOut, isFlagViewAssignedToBlackedOut } from '../../../utils/qaFlagBlackout.util'
import './FlagResolutionDetails.content.css'
import QaFlagCommentsBlock from '../../../components/QaFlagCommentsBlock.component'

interface IFlagDetailRow {
  label: string
  value: string | undefined
}

function FlagDetailRow({ label, value }: IFlagDetailRow) {
  return (
    <IonRow className='qa-addendum-detail-row'>
      <IonCol size='5'>
        <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{label}:</IonLabel>
      </IonCol>
      <IonCol size='7'>
        <IonLabel className='qa-addendum-detail-row-value fw-400 font-size-16'>{value}</IonLabel>
      </IonCol>
    </IonRow>
  )
}

interface IFlagResolutionDetailsContentProps {
  isExpanded?: boolean
  onReassignClick: () => void
  onAssignmentHistoryClick: () => void
}

const FlagResolutionDetailsContent: React.FC<IFlagResolutionDetailsContentProps> = ({
  isExpanded = false,
  onReassignClick,
  onAssignmentHistoryClick,
}) => {
  const { t } = useTranslation()
  const flag = useSelector(getSelectedFlagDetailSelector)
  const currentUserId = useSelector(getUserIdSelector)
  const canViewFlag = useSelector(canViewFlagSelector)
  const canAssignFlag = useSelector(canAssignFlagSelector)

  if (!flag) return null

  const isCreatorBlackedOut = isFlagViewCreatorBlackedOut(flag.lockType, canViewFlag)

  const privilegedRows: IFlagDetailRow[] = [
    { label: t('qaFlags.qaAddendum.flagResolution.dateTime'), value: formatShortMonthDateTime(flag.createdAt) },
    { label: t('qaFlags.qaAddendum.flagResolution.prid'), value: flag.prid },
    {
      label: t('qaFlags.qaAddendum.flagResolution.flaggedBy'),
      value: isCreatorBlackedOut ? t('qaFlags.qaAddendum.flagResolution.blackout.username') : flag.createdBy?.name,
    },
    { label: t('qaFlags.qaAddendum.flagResolution.flagType'), value: flag.flagType?.name },
    { label: t('qaFlags.qaAddendum.flagResolution.responseType'), value: flag.responseType?.name },
  ]

  const alwaysRows: IFlagDetailRow[] = [
    { label: t('qaFlags.qaAddendum.flagResolution.chartSection'), value: flag.referencePage },
  ]

  const assignedByName = flag.assignedBy?.name
    ? isCreatorBlackedOut
      ? t('qaFlags.qaAddendum.flagResolution.blackout.username')
      : flag.assignedBy.name
    : undefined
  const assignedAt = flag.assignedDt
  const hasAssignmentHistory = (flag.assignmentHistory?.length ?? 0) > 0
  const isExclusive = flag.crewSpecific

  const rootClassName = isExpanded
    ? 'qa-addendum-flag-resolution-details qa-addendum-flag-resolution-details-focused'
    : 'qa-addendum-flag-resolution-details'

  return (
    <div className={rootClassName}>
      <div className='qa-addendum-surface'>
        <div className='qa-addendum-details-section'>
          <IonItem
            button={!isExclusive && canAssignFlag}
            detail={false}
            lines='none'
            className='qa-addendum-assigned-to-item'
            onClick={!isExclusive && canAssignFlag ? onReassignClick : undefined}
            data-testid='AssignedToFieldTrigger'>
            <IonLabel>
              <IonText className='qa-addendum-assigned-to-label fw-400 font-size-14'>
                {t('qaFlags.qaAddendum.flagResolution.assignedTo')}
              </IonText>
              <div className='qa-addendum-assigned-to-value-row'>
                <IonText className='qa-addendum-assigned-to-value fw-700 font-size-16'>
                  {isFlagViewAssignedToBlackedOut(
                    flag.lockType,
                    flag.assignedTo?.id,
                    Number(currentUserId),
                    canViewFlag
                  )
                    ? t('qaFlags.qaAddendum.flagResolution.blackout.allCrew')
                    : flag.assignedTo?.name ?? ''}
                </IonText>
                {isExclusive && (
                  <span className='qa-addendum-exclusive-badge' data-testid='ExclusiveFlagBadge'>
                    <IonIcon icon={lockClosedOutline} aria-hidden='true' />
                    {t('qaFlags.qaAddendum.flagResolution.exclusive')}
                  </span>
                )}
              </div>
            </IonLabel>
            {!isExclusive && canAssignFlag && (
              <IonIcon
                slot='end'
                icon={caretDownOutline}
                className='qa-addendum-assigned-to-chevron'
                aria-hidden='true'
              />
            )}
          </IonItem>
          <hr className='field-divider qa-addendum-assigned-to-divider' />
          {canAssignFlag && assignedByName && assignedAt && (
            <IonItem
              data-testid='AssignedByHistory'
              button={hasAssignmentHistory && !isCreatorBlackedOut}
              detail={false}
              lines='none'
              className={`qa-addendum-assigned-by-item${
                hasAssignmentHistory && !isCreatorBlackedOut ? ' cursor-pointer' : ''
              }`}
              onClick={hasAssignmentHistory && !isCreatorBlackedOut ? onAssignmentHistoryClick : undefined}>
              <IonLabel>
                <IonRow className='qa-addendum-detail-row qa-addendum-detail-row-top'>
                  <IonCol size='5'>
                    <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>
                      {t('qaFlags.qaAddendum.flagResolution.assignedBy')}
                    </IonLabel>
                  </IonCol>
                  <IonCol size='7' className='qa-addendum-assigned-by-value-col'>
                    <div className='qa-addendum-value-stack'>
                      <IonLabel className='qa-addendum-detail-row-value fw-400 font-size-16'>{assignedByName}</IonLabel>
                      <IonText className='qa-addendum-value-meta fw-400 font-size-15'>
                        {formatShortMonthDateTime(assignedAt)}
                      </IonText>
                    </div>
                    {hasAssignmentHistory && !isCreatorBlackedOut && (
                      <IonIcon
                        icon={chevronForwardOutline}
                        className='qa-addendum-assigned-by-chevron'
                        aria-hidden='true'
                      />
                    )}
                  </IonCol>
                </IonRow>
              </IonLabel>
            </IonItem>
          )}
          <IonGrid className='ion-no-padding'>
            {canAssignFlag && privilegedRows.map((row) => <FlagDetailRow key={row.label} {...row} />)}
            {alwaysRows.map((row) => (
              <FlagDetailRow key={row.label} {...row} />
            ))}
          </IonGrid>
        </div>
        <QaFlagCommentsBlock
          text={flag.comments}
          commentsLabel={t('qaFlags.qaAddendum.flagResolution.comments')}
          isExpanded={isExpanded}
        />
      </div>
    </div>
  )
}

export default React.memo(FlagResolutionDetailsContent)
